import { A11y, Autoplay, EffectCards, EffectFade, FreeMode, Mousewheel, Navigation, Pagination, Scrollbar } from 'swiper/modules';

import {
    A11Y_ELEMENT_STYLES,
    AUTOPLAY_ELEMENT_STYLES,
    EFFECT_CARDS_ELEMENT_STYLES,
    EFFECT_FADE_ELEMENT_STYLES,
    FREE_MODE_ELEMENT_STYLES,
    NAVIGATION_ELEMENT_STYLES,
    PAGINATION_ELEMENT_STYLES,
    SCROLLBAR_ELEMENT_STYLES,
} from './swiper-element-styles';
import { SwiperThemes } from './swiper.types';

/**
 * The built-in theme presets of the shared library. Each theme bundles the Swiper options, the
 * Swiper modules those options rely on and the element styles that must be injected into the
 * shadow DOM (the shared library uses the core build of Swiper Element, which ships no module
 * styles of its own — see `swiper-element-styles.ts`).
 *
 * Apps can extend or override this registry via `provideSwiper({ themes: { … } })` or
 * `provideSwiperThemes({ … })`.
 */
export const SWIPER_BUILT_IN_THEMES: SwiperThemes = {
    /** A plain, single-slide carousel with navigation arrows and clickable pagination bullets. */
    default: {
        options: {
            slidesPerView: 1,
            spaceBetween: 16,
            navigation: true,
            pagination: {
                clickable: true,
            },
        },
        modules: [A11y, Navigation, Pagination],
        injectStyles: [A11Y_ELEMENT_STYLES, NAVIGATION_ELEMENT_STYLES, PAGINATION_ELEMENT_STYLES],
    },

    /** A free-scrolling strip of auto-sized slides with a draggable scrollbar — e.g. image galleries. */
    gallery: {
        options: {
            slidesPerView: 'auto',
            spaceBetween: 12,
            freeMode: true,
            scrollbar: {
                draggable: true,
            },
        },
        modules: [A11y, FreeMode, Scrollbar],
        injectStyles: [A11Y_ELEMENT_STYLES, FREE_MODE_ELEMENT_STYLES, SCROLLBAR_ELEMENT_STYLES],
    },

    /**
     * A horizontally scrollable strip of auto-sized items for navigation menus and tab bars.
     * Scrolls freely via drag, trackpad and mouse wheel (axis-locked, releases at the edges so
     * page scrolling is never trapped); arrows appear only when the items overflow.
     */
    menu: {
        options: {
            slidesPerView: 'auto',
            spaceBetween: 8,
            freeMode: {
                enabled: true,
                momentumBounce: false,
            },
            mousewheel: {
                forceToAxis: true,
                releaseOnEdges: true,
            },
            navigation: true,
        },
        modules: [A11y, FreeMode, Mousewheel, Navigation],
        injectStyles: [A11Y_ELEMENT_STYLES, FREE_MODE_ELEMENT_STYLES, NAVIGATION_ELEMENT_STYLES],
    },

    /** A looping, auto-playing, cross-fading stage slider with arrows — e.g. hero banners. Opt out of arrows per instance via `navigation: false`. */
    hero: {
        options: {
            slidesPerView: 1,
            loop: true,
            effect: 'fade',
            fadeEffect: {
                crossFade: true,
            },
            autoplay: {
                delay: 5000,
                pauseOnMouseEnter: true,
            },
            pagination: {
                clickable: true,
            },
            navigation: true,
        },
        modules: [A11y, Autoplay, EffectFade, Navigation, Pagination],
        injectStyles: [A11Y_ELEMENT_STYLES, AUTOPLAY_ELEMENT_STYLES, EFFECT_FADE_ELEMENT_STYLES, NAVIGATION_ELEMENT_STYLES, PAGINATION_ELEMENT_STYLES],
    },

    /** A stacked-cards effect with a grab cursor — e.g. swipeable teaser decks. */
    cards: {
        options: {
            effect: 'cards',
            grabCursor: true,
        },
        modules: [A11y, EffectCards],
        injectStyles: [A11Y_ELEMENT_STYLES, EFFECT_CARDS_ELEMENT_STYLES],
    },
};
